import { faEye, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Button, Card, CardHeader, ListGroup, ListGroupItem } from "reactstrap";
import { LookupSongFunction } from "../common/LookupSongFunction";
import { formatDateTimeString } from "../helper/formatDateTime";
import { usePlaylists } from "../playlist/graphql/usePlaylists";
import { PlaylistRoutePaths } from "../playlist/PlaylistRoutePaths";
import {
  forgetRecentPreviews,
  useRecentPreviews,
} from "../playlist/preview/RecentPreviews";
import { useSavePreviewAsOwnPlaylist } from "../playlist/preview/useSavePreviewAsOwnPlaylist";
import { CyPlaylistIndex } from "./CyPlaylistIndex";

interface Props {
  lookupSong: LookupSongFunction;
}

/**
 * The last previews opened from a link or QR code, on top of the playlist index in every view, as they belong to no
 * crew. A preview drops out as soon as the playlist with its id is among the playlists of the user.
 */
export function RecentPreviewsCard({ lookupSong }: Props) {
  const { t } = useTranslation();
  const recentPreviews = useRecentPreviews();
  const playlists = usePlaylists();
  const saveAsOwnPlaylist = useSavePreviewAsOwnPlaylist();

  const idsOfPlaylists = useMemo(
    () => new Set(playlists?.map((p) => p.id)),
    [playlists],
  );

  useEffect(() => {
    forgetRecentPreviews(
      recentPreviews.filter((p) => idsOfPlaylists.has(p.id)).map((p) => p.id),
    );
  }, [recentPreviews, idsOfPlaylists]);

  const previews = recentPreviews.filter((p) => !idsOfPlaylists.has(p.id));
  if (previews.length === 0) {
    return null;
  }

  return (
    <Card className="mb-3" data-cy={CyPlaylistIndex.RecentPreviews}>
      <CardHeader>
        <FontAwesomeIcon icon={faEye} /> {t("playlist.preview.recentTitle")}
        <div className="small text-body-secondary">
          {t("playlist.preview.recentHint")}
        </div>
      </CardHeader>
      <ListGroup flush>
        {previews.map((preview) => (
          <ListGroupItem
            key={preview.id}
            className="d-flex align-items-center gap-2"
            data-cy={CyPlaylistIndex.RecentPreview}
          >
            <div className="flex-grow-1" style={{ minWidth: 0 }}>
              <Link to={PlaylistRoutePaths.toCarousel(preview.id)}>
                {t("playlist.preview.defaultTitle", {
                  date: formatDateTimeString(preview.openedAt),
                })}
              </Link>
              <div className="small text-body-secondary text-truncate">
                {preview.songIds
                  .map((id) => lookupSong(id)?.title)
                  .filter((title) => title !== undefined)
                  .join(", ")}
              </div>
            </div>
            <Button
              color="link"
              size="sm"
              onClick={() => saveAsOwnPlaylist(preview)}
              data-cy={CyPlaylistIndex.ButtonSaveRecentPreview}
            >
              {t("common.buttons.save")}
            </Button>
            <Button
              color="link"
              size="sm"
              className="link-secondary"
              aria-label={t("playlist.preview.remove")}
              title={t("playlist.preview.remove")}
              onClick={() => forgetRecentPreviews([preview.id])}
              data-cy={CyPlaylistIndex.ButtonRemoveRecentPreview}
            >
              <FontAwesomeIcon icon={faTimes} />
            </Button>
          </ListGroupItem>
        ))}
      </ListGroup>
    </Card>
  );
}
